import { FaroMenuItem } from "@components/common/faro-select/faro-menu-item";
import { FaroTextMenuButton } from "@components/common/faro-text-menu-button";
import { SphereDivider } from "@components/common/sphere-divider";
import { Text, Checkmark2Icon, blue, neutral } from "@flat-ui";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, FormControl, Grid2, ListSubheader, Stack } from "@mui/material";
import { useAccountSettingsLayout } from "@pages/account-settings/account-settings-layout/account-settings-layout-utils";
import { colorConst, EDecimalToHex } from "@styles/common-colors";
import {
  DEFAULT_TEXT_FONT_SIZE,
  DEFAULT_TEXT_FONT_WEIGHT,
} from "@styles/common-styles";
import { addTransparency } from "@utils/ui-utils";

export interface IItemProps<T = string> {
  /** The value representing the item */
  value: T;

  /** The label that is shown for the item */
  label: string;

  /** Any description shown  */
  description?: string;

  /** Determines if the item is a header of a section */
  isHeader?: boolean;
}

interface Props<T> {
  /** If true it will show the description in the title as well */
  shouldShowDescriptionInTitle?: boolean;

  /** List or items shown in dropdown */
  items: IItemProps<T>[];

  /** The selected item */
  selectedItem: IItemProps<T>;

  /** On click handler for when selecting an item */
  onClickHandler: (value: T) => Promise<void> | void;
}

/** Header margin when there is a divider */
const MARGIN_TOP_WITH_DIVIDER = 2.5;

/** Class name for item label */
const ITEM_LABEL_CLASS = "item-label";

/** A dropdown to show for account settings */
export function AccountSettingsDropdown<T extends string>({
  shouldShowDescriptionInTitle = false,
  items,
  selectedItem,
  onClickHandler,
}: Props<T>): JSX.Element {
  const { isHoverSupported, isScreenSmOrSmaller } = useMediaQueryList();
  const { accountSettingsFontSize, accountSettingsFontWeight } =
    useAccountSettingsLayout();

  const ButtonTitle = (
    <Grid2 container justifyContent="space-between" alignItems="center">
      <Box
        sx={{
          fontSize: accountSettingsFontSize,
          fontWeight: accountSettingsFontWeight,
        }}
      >
        {selectedItem.label}
      </Box>
      {shouldShowDescriptionInTitle && !isScreenSmOrSmaller && (
        <Box
          sx={{
            fontSize: DEFAULT_TEXT_FONT_SIZE,
            fontWeight: DEFAULT_TEXT_FONT_WEIGHT,
          }}
        >
          {selectedItem.description}
        </Box>
      )}
    </Grid2>
  );

  return (
    <FormControl
      sx={{
        width: "100%",
      }}
    >
      <FaroTextMenuButton
        buttonTitle={ButtonTitle}
        isMenuWidthEqualToButton={true}
        buttonSx={(isMenuOpen) => {
          return {
            color: isMenuOpen ? blue[500] : neutral[800],
            width: "100%",
            textOverflow: "ellipsis",
            justifyContent: "space-between",
            "& .MuiButton-icon": {
              "& svg": { width: "16px", height: "16px" },
            },

            // Not focused
            "&:not(&:focus)": {
              borderBottom: isHoverSupported
                ? "none"
                : `1px solid ${neutral[200]}`,

              "& .MuiButton-icon": {
                visibility: isHoverSupported ? "hidden" : "visible",
              },

              // Hover style. Show underline and icon
              "&:hover": {
                backgroundColor: addTransparency({
                  color: neutral[500],
                  alpha: EDecimalToHex.thirtyEight,
                }),
                color: blue[500],

                "& .MuiButton-icon": {
                  visibility: "visible",
                },
              },
            },

            // Focused
            "&:focus": {
              borderBottom: `2px solid ${blue[500]}`,
              color: blue[500],
            },
          };
        }}
      >
        {items.map((item, index) => {
          const isSelectedItem = item.value === selectedItem.value;

          if (item.isHeader) {
            return (
              <ListSubheader
                key={index}
                sx={{ backgroundColor: "transparent", p: 1.5 }}
              >
                {index !== 0 && <SphereDivider />}

                <Text
                  component="div"
                  variant="labelS"
                  sx={{
                    mt: index === 0 ? 1 : MARGIN_TOP_WITH_DIVIDER,
                  }}
                >
                  {item.label}
                </Text>
              </ListSubheader>
            );
          }

          return (
            <FaroMenuItem
              data-my-value={item.value}
              key={item.value}
              value={item.value}
              onClick={(event) => {
                if (event.currentTarget.dataset.myValue) {
                  void onClickHandler(event.currentTarget.dataset.myValue as T);
                }
              }}
              sx={{
                display: "block",
                padding: "8px 12px",
                mb: "2px",
                whiteSpace: "normal",
                "&:hover": {
                  backgroundColor: addTransparency({
                    color: neutral[500],
                    alpha: EDecimalToHex.twentySix,
                  }),

                  // Set menu item label color to blue500 on hover
                  [`& .${ITEM_LABEL_CLASS}`]: {
                    color: blue[500],
                  },
                },
                "&.MuiButtonBase-root": {
                  backgroundColor: isSelectedItem
                    ? addTransparency({
                        color: neutral[500],
                        alpha: EDecimalToHex.thirtyEight,
                      })
                    : "transparent",

                  "&:hover": {
                    backgroundColor: addTransparency({
                      color: neutral[500],
                      alpha: EDecimalToHex.thirtyEight,
                    }),
                  },

                  // Overrides default style for ".Mui-focusVisible" class
                  "&.Mui-focusVisible": {
                    backgroundColor: addTransparency({
                      color: neutral[500],
                      alpha: EDecimalToHex.thirtyEight,
                    }),
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Stack direction="row" gap={1.5} alignItems="flex-end">
                  {/* Title */}
                  <Text
                    variant="bodyM"
                    className={ITEM_LABEL_CLASS}
                    sx={{
                      color: isSelectedItem ? blue[500] : colorConst.normalFont,
                    }}
                  >
                    {item.label}
                  </Text>

                  {/* Description */}
                  <Text
                    variant="bodyS"
                    sx={{
                      color: isSelectedItem ? blue[500] : neutral[600],
                    }}
                  >
                    {item.description}
                  </Text>
                </Stack>

                {/* Icon */}
                {isSelectedItem && (
                  <Checkmark2Icon
                    sx={{
                      color: blue[500],
                      width: "18px",
                      height: "18px",
                    }}
                  />
                )}
              </Box>
            </FaroMenuItem>
          );
        })}
      </FaroTextMenuButton>
    </FormControl>
  );
}
